import { create } from 'zustand'
import { authApi, teamApi } from '../lib/api'

const useOrgStore = create((set, get) => ({
  // --- State ---
  org: null,
  usage: null,
  members: [],
  pendingInvites: [],
  loadingOrg: false,
  loadingMembers: false,
  error: null,

  // --- Org ---
  fetchOrg: async () => {
    set({ loadingOrg: true, error: null })
    try {
      const data = await authApi.getOrg()
      set({
        org: data.org,
        loadingOrg: false,
      })
      return data
    } catch (error) {
      set({ error: error.message, loadingOrg: false })
      return null
    }
  },

  fetchUsage: async () => {
    try {
      const data = await authApi.getUsage()
      const usage = data.usage || data
      set({ usage: {
        activeJobs: parseInt(usage.activeJobs || 0),
        maxJobs: usage.maxJobs,
        jobUsagePercent: usage.jobUsagePercent || 0,
        monthlyCVs: parseInt(usage.monthlyCVs || 0),
        maxCVsPerMonth: usage.maxCVsPerMonth,
        cvUsagePercent: usage.cvUsagePercent || 0,
        resetDate: usage.resetDate,
        canCreateJob: data.canCreateJob,
        canUploadCV: data.canUploadCV,
        plan: data.plan,
        maxMembers: usage.maxMembers,
        memberCount: usage.memberCount,
      }})
      return data
    } catch (error) {
      set({ error: error.message })
      return null
    }
  },

  updateOrg: async (updates) => {
    try {
      await authApi.updateOrg(updates)
      await get().fetchOrg()
      return { success: true }
    } catch (error) {
      return { success: false, error: error.message }
    }
  },

  // --- Members ---
  fetchMembers: async () => {
    set({ loadingMembers: true, error: null })
    try {
      const data = await teamApi.listMembers()
      set({
        members: data.members || [],
        pendingInvites: data.pendingInvites || [],
        loadingMembers: false,
      })
      return data
    } catch (error) {
      set({ error: error.message, loadingMembers: false })
      return null
    }
  },

  inviteMember: async (email, role) => {
    try {
      const data = await teamApi.inviteMember(email, role)
      await get().fetchMembers()
      return { success: true, data }
    } catch (error) {
      return { success: false, error: error.message }
    }
  },

  updateMemberRole: async (userId, role) => {
    try {
      await teamApi.updateRole(userId, role)
      set((state) => ({
        members: state.members.map((m) =>
          m.userId === userId ? { ...m, role } : m
        ),
      }))
      return { success: true }
    } catch (error) {
      return { success: false, error: error.message }
    }
  },

  removeMember: async (userId) => {
    try {
      await teamApi.removeMember(userId)
      set((state) => ({
        members: state.members.filter((m) => m.userId !== userId),
      }))
      return { success: true }
    } catch (error) {
      return { success: false, error: error.message }
    }
  },

  // --- Plan helpers ---
  isAtJobLimit: () => {
    const { usage } = get()
    if (!usage) return false
    if (usage.maxJobs === -1) return false
    return usage.activeJobs >= usage.maxJobs
  },

  isAtCVLimit: () => {
    const { usage } = get()
    if (!usage) return false
    if (usage.maxCVsPerMonth === -1) return false
    return usage.monthlyCVs >= usage.maxCVsPerMonth
  },

  isAtMemberLimit: () => {
    const { usage, members } = get()
    if (!usage) return false
    if (usage.maxMembers === -1) return false
    return members.length >= usage.maxMembers
  },

  canUpgrade: () => {
    const { org } = get()
    if (!org) return false
    return !['business', 'enterprise'].includes(org.plan)
  },

  // --- Clear ---
  clearError: () => set({ error: null }),
  reset: () =>
    set({
      org: null,
      usage: null,
      members: [],
      pendingInvites: [],
      error: null,
    }),
}))

export default useOrgStore
